import React, { useState, useEffect, useRef, useCallback } from 'react';
import BreadcrumbsNav from './BreadcrumbsNav';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Link,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';

// Icons
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useShoppingMode } from './ShoppingModeContext';

interface TokenPayload {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
}

interface HeaderProps {
  toggleTheme: () => void;
  currentMode: 'light' | 'dark';
  onLogout: () => void;
  resetThemeToSystem: () => void;
  hasManualThemePreference: boolean;
}

const Header: React.FC<HeaderProps> = ({ toggleTheme, currentMode, onLogout, resetThemeToSystem, hasManualThemePreference }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'));
  const [firstName, setFirstName] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Sticky toolbar state
  const [isToolbarSticky, setIsToolbarSticky] = useState(false);
  const toolbarPlaceholderRef = useRef<HTMLDivElement>(null);
  const toolbarOriginalTop = useRef<number | null>(null);

  const handleScroll = useCallback(() => {
    if (!toolbarPlaceholderRef.current) return;

    // Calculate the original position on first scroll
    if (toolbarOriginalTop.current === null) {
      const rect = toolbarPlaceholderRef.current.getBoundingClientRect();
      toolbarOriginalTop.current = rect.top + window.scrollY;
    }

    // Check if we've scrolled past the toolbar's original position
    const shouldStick = window.scrollY >= toolbarOriginalTop.current;
    setIsToolbarSticky(shouldStick);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once on mount to handle pages that load already scrolled
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    // Listen for authentication changes (e.g., login/logout).
    const handleAuthChange = () => {
      setToken(localStorage.getItem('access_token'));
    };
    window.addEventListener('authChange', handleAuthChange);
    return () => {
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    // Decode the token to extract the user's first name or username.
    if (!token) {
      setFirstName(null);
      setIsAdmin(false);
      return;
    }
    try {
      const decoded = jwtDecode<TokenPayload>(token);
      setFirstName(decoded.first_name || decoded.username);
      setIsAdmin(decoded.is_staff || decoded.is_superuser);
    } catch (err) {
      // Handle invalid or expired token by logging the user out.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.dispatchEvent(new Event('authChange'));
      onLogout();
      navigate('/login?message=session_expired');
    }
  }, [token, navigate, onLogout]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.dispatchEvent(new Event('authChange'));
    onLogout();
    navigate('/');
  };

  return (
    <>
      {/* Banner - scrolls with page */}
      <HeaderBanner isMobile={isMobile} currentMode={currentMode} />

      {/* Toolbar - sticky at top when scrolled */}
      <HeaderToolbar
        toggleTheme={toggleTheme}
        currentMode={currentMode}
        onLogout={handleLogout}
        resetThemeToSystem={resetThemeToSystem}
        hasManualThemePreference={hasManualThemePreference}
        token={token || undefined}
        firstName={firstName || undefined}
        isAdmin={isAdmin}
        isMobile={isMobile}
        isSticky={isToolbarSticky}
        placeholderRef={toolbarPlaceholderRef}
      />
    </>
  );
};

// Component for the header banner, including the logo and title.
const HeaderBanner: React.FC<{
  isMobile: boolean;
  currentMode: 'light' | 'dark';
}> = ({ isMobile, currentMode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mr: isMobile ? 0 : 1 }}>
          <RouterLink to="/">
            <img
              src={`${process.env.REACT_APP_IMAGES_URL}${currentMode === 'dark' ? '/dark' : ''}/logo.svg`}
              alt="Logo"
              style={
                isMobile
                  ? { height: '70px', objectFit: 'contain' }
                  : { height: '100px', objectFit: 'contain' }
              }
            />
          </RouterLink>
        </Box>
        <Typography
          variant={isMobile ? 'h4' : 'h2'}
          color="primary"
          component="div"
          sx={(theme) => ({
            fontFamily: '"Lobster Two", cursive',
            textShadow: `${
              currentMode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]
            } 4px 4px`,
          })}
        >
          Grocery Price Checker
        </Typography>
      </Container>
    </Box>
  );
};

// Component for the header toolbar, including breadcrumbs, account actions, and theme toggle.
const HeaderToolbar: React.FC<{
  toggleTheme: () => void;
  currentMode: 'light' | 'dark';
  onLogout: () => void;
  resetThemeToSystem: () => void;
  hasManualThemePreference: boolean;
  token?: string;
  firstName?: string;
  isAdmin: boolean;
  isMobile: boolean;
  isSticky: boolean;
  placeholderRef: React.RefObject<HTMLDivElement>;
}> = ({ toggleTheme, currentMode, onLogout, resetThemeToSystem, hasManualThemePreference, token, firstName, isAdmin, isMobile, isSticky, placeholderRef }) => {
  const theme = useTheme();
  const { shoppingMode } = useShoppingMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    onLogout();
  };

  const handleThemeToggle = () => {
    handleMenuClose();
    toggleTheme();
  };

  const handleResetTheme = () => {
    handleMenuClose();
    resetThemeToSystem();
  };

  const appBarContent = (
    <AppBar
      position={isSticky ? 'fixed' : 'static'}
      color="primary"
      sx={{
        top: 0,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Toolbar variant="dense" sx={{ justifyContent: 'space-between' }}>
          <BreadcrumbsNav />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {shoppingMode.isActive && (
            <Chip
              icon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
              label="Shopping"
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/going-shopping"
              state={{ goToActiveList: true }}
              clickable
              sx={{
                mr: 1,
                height: 22,
                fontSize: '0.7rem',
                color: 'inherit',
                borderColor: 'currentColor',
                '& .MuiChip-icon': {
                  color: 'inherit',
                  marginLeft: '4px',
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            />
          )}
          {token && firstName ? (
            isMobile ? (
              // Mobile: Three-dot menu
              <>
                <IconButton color="inherit" onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem component={RouterLink} to="/account" onClick={handleAccountClick}>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{firstName} (Account)</ListItemText>
                  </MenuItem>
                  {isAdmin && (
                    <MenuItem component={RouterLink} to="/admin" onClick={handleMenuClose}>
                      <ListItemIcon>
                        <AdminPanelSettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Admin</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleThemeToggle}>
                    <ListItemIcon>
                      {currentMode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>{currentMode === 'dark' ? 'Light Mode' : 'Dark Mode'}</ListItemText>
                  </MenuItem>
                  {hasManualThemePreference && (
                    <MenuItem onClick={handleResetTheme}>
                      <ListItemIcon>
                        <SettingsBrightnessIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Use System Theme</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogoutClick}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              // Desktop: Individual icons
              <>
                <Tooltip title="Account details" arrow>
                  <Button
                    color="inherit"
                    component={RouterLink}
                    to="/account"
                    sx={{
                      textTransform: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      mr: 1,
                    }}
                  >
                    <PersonIcon sx={{ mr: 0.5 }} /> {firstName}
                  </Button>
                </Tooltip>
                <Tooltip title="Logout" arrow>
                  <IconButton color="inherit" onClick={onLogout}>
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={currentMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  arrow
                >
                  <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 1 }}>
                    {currentMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Tooltip>
                {hasManualThemePreference && (
                  <Tooltip title="Use system theme" arrow>
                    <IconButton onClick={resetThemeToSystem} color="inherit">
                      <SettingsBrightnessIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {isAdmin && (
                  <Tooltip title="Admin" arrow>
                    <IconButton color="inherit" component={RouterLink} to="/admin" sx={{ ml: 1 }}>
                      <AdminPanelSettingsIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )
          ) : (
            // Not logged in
            <>
              <Link
                component={RouterLink}
                to="/login"
                color="inherit"
                underline="hover"
                sx={{ textDecoration: 'none' }}
              >
                <Typography variant="body2" color="inherit" sx={{ fontSize: 'inherit' }}>
                  Login
                </Typography>
              </Link>
              <Tooltip
                title={currentMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                arrow
              >
                <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 1 }}>
                  {currentMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Tooltip>
              {hasManualThemePreference && (
                <Tooltip title="Use system theme" arrow>
                  <IconButton onClick={resetThemeToSystem} color="inherit">
                    <SettingsBrightnessIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Toolbar>
      </Container>
    </AppBar>
  );

  return (
    <>
      {/* Placeholder to maintain layout and track position */}
      <Box
        ref={placeholderRef}
        sx={{
          height: isSticky ? 48 : 0,
          transition: 'height 0.15s ease-out',
        }}
      />
      {appBarContent}
    </>
  );
};

export default Header;